import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Divider,
  CircularProgress,
} from '@mui/material';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import RefreshIcon from '@mui/icons-material/Refresh';

interface Props {
  agent:any;
  originalPrompt: string;
  enhancedPrompt: string;
  isOpen: boolean;
  isEnhancing?: boolean;
  onAccept: (finalPrompt: string) => void;
  onReject: () => void;
  onRegenerate: () => void; 
}

const PromptEnhancerDialog: React.FC<Props> = ({
  originalPrompt,
  enhancedPrompt,
  isOpen,
  isEnhancing = false,
  onAccept,
  onReject,
  onRegenerate,
}) => {
  // Local state to allow the user to edit the enhanced prompt before accepting
  const [editableEnhancedPrompt, setEditableEnhancedPrompt] = useState(enhancedPrompt);

  // Update local state whenever the external enhancedPrompt prop changes (e.g., after regeneration)
  React.useEffect(() => {
    setEditableEnhancedPrompt(enhancedPrompt);
  }, [enhancedPrompt]);

  const handleAccept = () => {
    // Pass the potentially edited prompt back to the parent
    onAccept(editableEnhancedPrompt);
  };

  const handleRejectAndClose = () => {
    onReject();
  };

  return (
    <Dialog
      open={isOpen}
      onClose={handleRejectAndClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="enhancement-dialog-title"
      PaperProps={{
    sx: {
      minHeight: '80vh', // Apply the style here
    },
  }}
    >
      <DialogTitle id="enhancement-dialog-title">{originalPrompt ?  "Review Enhanced Prompt" : "Review Suggested Prompt"}</DialogTitle>
      <DialogContent dividers>
        {/* Original Prompt (Context) */}
        <Box mb={3}>
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              padding: '4px 12px',
              mb: 1,
              borderRadius: '16px',
              border: '1px solid',
              borderColor: 'text.secondary',
              color: 'text.secondary',
              fontSize: '0.8rem',
              fontWeight: 'medium',
            }}
          >
            Original Prompt
          </Box>
          <TextField
            multiline
            rows={3}
            fullWidth
            value={originalPrompt}
            InputProps={{ readOnly: true }}
            variant="outlined"
            sx={{ backgroundColor: 'action.hover' }} // Subtle gray background for read-only
          />
        </Box>

        <Divider />

        {/* Enhanced Prompt */}
        <Box mt={3}>
          <Typography variant="h6" gutterBottom>
            {originalPrompt ?  "Enhanced Prompt" : "Suggested Prompt"}
          </Typography>
          {isEnhancing ? (
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: '300px',
                border: '1px solid',
                borderColor: 'primary.main',
                borderRadius: 1,
                backgroundColor: 'grey.50',
              }}
            >
              <CircularProgress size={40} sx={{ mb: 2 }} />
              <Typography variant="body2" color="text.secondary">
                {originalPrompt ? "Generating enhanced prompt..." : "Generating prompt suggestion..."}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                This may take a few seconds
              </Typography>
            </Box>
          ) : (
            <TextField
              multiline
              rows={12}
              fullWidth
              value={editableEnhancedPrompt}
              onChange={(e) => setEditableEnhancedPrompt(e.target.value)}
              placeholder="AI-generated enhanced prompt will appear here."
              variant="outlined"
              sx={{
                  '& .MuiOutlinedInput-root': {
                      border: '1px solid',
                      borderColor: 'primary.main',
                  },
              }}
            />
          )}
          <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
            {isEnhancing ? 'Please wait while we enhance your prompt...' : 'You may edit the enhanced prompt before accepting.'}
          </Typography>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
        <Box>
          <Button 
            onClick={onRegenerate} 
            startIcon={<RefreshIcon />} 
            variant="outlined" 
            color="primary"
            disabled={isEnhancing}
          >
            {isEnhancing ? 'Regenerating...' : 'Regenerate'}
          </Button>
        </Box>
        <Box>
          <Button variant="outlined" onClick={handleRejectAndClose} startIcon={<CloseIcon />}>
            Cancel
          </Button>
          <Button
            onClick={handleAccept}
            startIcon={<CheckIcon />}
            variant="contained"
            color="primary"
            sx={{ ml: 2 }}
            disabled={isEnhancing || !editableEnhancedPrompt.trim()}
          >
            Accept and Replace
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default PromptEnhancerDialog;